import {
  AUDIT_FIELDS,
  GROUP_FIELDS,
  USER_FIELDS,
} from "@/modules/settings/fields";
import { AuditLog } from "@/modules/settings/screens/AuditLog";
import { CompanySettings } from "@/modules/settings/screens/CompanySettings";
import { DashboardSettings } from "@/modules/settings/screens/DashboardSettings";
import { GroupsList } from "@/modules/settings/screens/GroupsList";
import { LogisticsPartners } from "@/modules/settings/screens/LogisticsPartners";
import { EscalationEmails } from "@/modules/settings/screens/EscalationEmails";
import { NotificationSchedule } from "@/modules/settings/screens/NotificationSchedule";
import { OrderStages } from "@/modules/settings/screens/OrderStages";
import { PushSettings } from "@/modules/settings/screens/PushSettings";
import { ReturnsSettings } from "@/modules/settings/screens/ReturnsSettings";
import { RoleBuilder } from "@/modules/settings/screens/RoleBuilder";
import { SavedViewsAdmin } from "@/modules/settings/screens/SavedViewsAdmin";
import { ShopifySync } from "@/modules/settings/screens/ShopifySync";
import { Tags } from "@/modules/settings/screens/Tags";
import { UsersList } from "@/modules/settings/screens/UsersList";
import type { ModuleUi } from "@/modules/types";

/**
 * The Settings module (M8-9 … M8-14).
 *
 * Three shapes among its eight sections: `users`, `groups` and `audit` are
 * lists and declare a field set each, because each renders a different row
 * (`User`, `Group`, `AuditEntry`) — the same reason Orders and Contacts
 * split their own multi-section fields. `roles`, `company` and `tags`
 * declare `fields: []`: a role builder, a settings form and the tag
 * vocabulary's rename/merge/delete actions are not filterable lists, the
 * same reason `hsn-summary` and `exports` are empty in
 * `modules/gst/index.ts`. `views` also declares `fields: []` — it reads
 * across every scope rather than one `list(query)` call, so there is no
 * single vocabulary for the shared toolbar to filter or sort against; see
 * the comment at the top of each screen for what backs it instead.
 *
 * `sync` declares `fields: []` for a different reason: it renders a
 * placeholder status board *and* the real channel write log side by side,
 * and the shared toolbar has no way to filter one without touching the
 * other. `ShopifySync` declares `CHANNEL_WRITE_FIELDS` itself and drives the
 * log's filter, sort and pagination from local state instead — see that
 * field vocabulary's doc comment in `fields.ts`.
 *
 * `logistics` (#527) declares `fields: []` as well: it is a page of partner
 * cards, a settings form and two short tables, none of which the shared
 * toolbar could filter. It reads and writes through `TrackingRepository`.
 *
 * `sync`'s status board has no write path in `SettingsRepository` — there is
 * no method for it yet at all; its channel write log is read-only because
 * nothing writes a `ChannelWrite` from this screen — it is the record of a
 * write that happened elsewhere.
 */
export const settingsModule: ModuleUi = {
  id: "settings",
  sections: {
    users: { fields: USER_FIELDS, screen: UsersList },
    groups: { fields: GROUP_FIELDS, screen: GroupsList },
    roles: { fields: [], screen: RoleBuilder },
    company: { fields: [], screen: CompanySettings, padded: true },
    views: { fields: [], screen: SavedViewsAdmin, padded: true },
    sync: { fields: [], screen: ShopifySync },
    logistics: { fields: [], screen: LogisticsPartners, padded: true },
    returns: { fields: [], screen: ReturnsSettings, padded: true },
    notifications: { fields: [], screen: NotificationSchedule, padded: true },
    "escalation-emails": {
      fields: [],
      screen: EscalationEmails,
      padded: true,
    },
    push: { fields: [], screen: PushSettings, padded: true },
    "order-stages": { fields: [], screen: OrderStages, padded: true },
    // The amber and red limits the dashboard colours its numbers by (#528).
    dashboard: { fields: [], screen: DashboardSettings, padded: true },
    tags: { fields: [], screen: Tags, padded: true },
    audit: { fields: AUDIT_FIELDS, screen: AuditLog },
  },
};
